import { useEffect, useRef, useState } from "react";

/**
 * Slow-motion, muted, looping background video.
 * - playbackRate < 1 gives the slow-motion feel
 * - IntersectionObserver pauses playback while off-screen (saves CPU/battery)
 * - prefers-reduced-motion users get the still poster image instead
 */
export default function VideoBackdrop({ sources, poster, alt = "", rate = 0.5, className = "", testId = "video-backdrop" }) {
  const ref = useRef(null);
  const [reduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  useEffect(() => {
    const video = ref.current;
    if (!video || reduced) return undefined;
    video.playbackRate = rate;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { rootMargin: "200px" }
    );
    io.observe(video);
    return () => io.disconnect();
  }, [rate, reduced]);

  const cls = `absolute inset-0 h-full w-full object-cover ${className}`;

  if (reduced) {
    return <img src={poster} alt={alt} loading="lazy" className={cls} data-testid={`${testId}-still`} />;
  }

  return (
    <video
      ref={ref}
      poster={poster}
      muted
      loop
      playsInline
      preload="metadata"
      aria-hidden="true"
      onLoadedMetadata={(e) => {
        e.currentTarget.playbackRate = rate;
      }}
      className={cls}
      data-testid={testId}
    >
      {sources.map((s) => (
        <source key={s.src} src={s.src} type={s.type} />
      ))}
    </video>
  );
}
